
import { useState, useMemo, useSyncExternalStore } from "react";
import { ChevronDown, ChevronRight, ChevronLeft, LogOut, HelpCircle, Users, Download, Heart, Swords, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Custom SVG Icons for Sidebar
const LobbyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const ForYouIcon = ({ className }: { className?: string }) => (
  <Heart className={className} />
);

const EndlessReelsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {/* Slot machine frame */}
    <rect x="2" y="4" width="20" height="16" rx="2" />
    {/* Three reel windows */}
    <rect x="4.5" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    <rect x="10" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    <rect x="15.5" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    {/* Reel symbols — center row (winning line) */}
    <circle cx="6.5" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
    <path d="M11 10.5l1 1.5 1-1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <circle cx="17.5" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
    {/* Win line */}
    <line x1="3" y1="12" x2="4.5" y2="12" strokeWidth="1" opacity="0.5" />
    <line x1="19.5" y1="12" x2="21" y2="12" strokeWidth="1" opacity="0.5" />
  </svg>
);

const LiveCasinoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M18 10l4-2v8l-4-2v-4z" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="20" cy="4" r="2" fill="#ef4444" stroke="#ef4444" />
  </svg>
);

const ChallengesIcon = ({ className }: { className?: string }) => (
  <Swords className={className} />
);

const HighPlayIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {/* Shield body */}
    <path d="M12 2l8 4v5c0 5.25-3.5 9.75-8 11-4.5-1.25-8-5.75-8-11V6l8-4z" />
    {/* Lightning bolt */}
    <path d="M13 7l-3 5h4l-3 5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const JackpotIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    <circle cx="12" cy="12" r="4" />
    <text x="9.5" y="14.5" fontSize="6" fill="currentColor" fontWeight="bold">$</text>
  </svg>
);

const LoyaltyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2L15 8.5L22 9.5L17 14.5L18 22L12 18.5L6 22L7 14.5L2 9.5L9 8.5L12 2Z" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.3" />
  </svg>
);

const ReferFriendsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="8" cy="8" r="4" />
    <path d="M2 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M16 11h6M19 8v6" />
  </svg>
);

const VaultIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <circle cx="12" cy="13" r="4" />
    <circle cx="12" cy="13" r="1.5" fill="currentColor" />
    <line x1="12" y1="9" x2="12" y2="10" />
    <line x1="12" y1="16" x2="12" y2="17" />
    <line x1="8" y1="13" x2="9" y2="13" />
    <line x1="15" y1="13" x2="16" y2="13" />
    <rect x="8" y="2" width="8" height="3" rx="1" />
  </svg>
);

const DailyBonusIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <path d="M12 14l1.5 3 3.5.5-2.5 2.5.5 3.5-3-1.5-3 1.5.5-3.5-2.5-2.5 3.5-.5z" fill="currentColor" opacity="0.3" />
  </svg>
);

const PromotionsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
  </svg>
);

import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { ColorThemeSelectorMini } from "@/components/ColorThemeSelector";
import { useLogoSrc } from "@/hooks/useLogoSrc";

import { useAuth } from "@/contexts/AuthContext";
import { useVip } from "@/contexts/VipContext";
import { useProfileQuery } from "@/hooks/queries/useProfileQuery";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { VipTierDisplay } from "./VipTierDisplay";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RedeemModal } from "./RedeemModal";
import { useReferralMaxScRewardQuery } from "@/hooks/queries/useReferralQueries";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDailyBonus } from "@/contexts/DailyBonusContext";
import { useChatOptional } from "@/contexts/ChatContext";
import { useGetCoins } from "@/contexts/GetCoinsContext";
import { useCategoriesQuery } from "@/hooks/queries/useCategoriesQuery";
import { useLiveCategoriesQuery } from "@/hooks/queries/useLiveCategoriesQuery";
import { useUserInitContactChannels } from "@/hooks/queries/useUserInitQuery";
import { useSupportChatVisibilityOptional } from "@/contexts/SupportChatVisibilityContext";

/** Breakpoint below which Get Coins / Redeem move from header into the sidebar */
const COMPACT_HEADER_BREAKPOINT_CHAT_OPEN = 1310;
const COMPACT_HEADER_BREAKPOINT_CHAT_CLOSED = 1050;

function subscribeToResize(cb: () => void) {
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}
function getWidth() { return window.innerWidth; }
function useWindowWidth() { return useSyncExternalStore(subscribeToResize, getWidth, () => 1400); }

interface SidebarItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path?: string;
  action?: string; // Special action identifier (e.g., 'daily-bonus')
  badge?: string | number;
  disabled?: boolean;
  /** If set, this item is hidden from the sidebar when the specified category section has no categories */
  requiresSection?: 'high_play' | 'live';
}

interface SidebarCategory {
  title: string;
  items: SidebarItem[];
}

const sidebarCategories: SidebarCategory[] = [
  {
    title: "Main",
    items: [
      { icon: LobbyIcon, label: "Lobby", path: "/home" },
      { icon: ForYouIcon, label: "For You", path: "/for-you" },
    ]
  },
  {
    title: "Social Games",
    items: [
      { icon: LiveCasinoIcon, label: "Live Experience", path: "/live-experience", requiresSection: 'live' },
      { icon: HighPlayIcon, label: "High Play", path: "/high-play", requiresSection: 'high_play' },
      { icon: EndlessReelsIcon, label: "Endless Reels", path: "/endless-reels" },
    ]
  },
  {
    title: "Exclusives", 
    items: [
      { icon: JackpotIcon, label: "Progressive Jackpot", path: "/jackpot" },
      { icon: LoyaltyIcon, label: "VIP Loyalty Club", path: "/loyalty-club" },
      { icon: ReferFriendsIcon, label: "Refer Friends", path: "/refer-friends" },
      { icon: VaultIcon, label: "Coin Vault", path: "/vault" },
      { icon: DailyBonusIcon, label: "Daily Bonus", action: "daily-bonus" },
      { icon: PromotionsIcon, label: "Promotions", path: "/promotions" },
      { icon: ChallengesIcon, label: "Challenges", path: "/challenges", disabled: true, badge: "Coming Soon" },
    ]
  },
  {
    title: "Support",
    items: [
      { icon: HelpCircle, label: "Help Center", path: "/help-center" },
      { icon: Download, label: "Install App", path: "/install-app" },
    ]
  }
];

interface CasinoSidebarNewProps {
  onRedeemClick?: () => void;
  onGetCoinsClick?: () => void;
  onDailyBonusClick?: () => void;
  onNavigate?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isMobileFullScreen?: boolean;
}

export const CasinoSidebarNew = ({ onRedeemClick, onGetCoinsClick, onDailyBonusClick, onNavigate, isCollapsed = false, onToggleCollapse, isMobileFullScreen = false }: CasinoSidebarNewProps = {} as CasinoSidebarNewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { getThemeColors, selectedCurrency } = useTheme();
  const logoSrc = useLogoSrc();
  const { openDailyBonusModal } = useDailyBonus();
  const chatContext = useChatOptional();
  const { openGetCoinsModal } = useGetCoins();
  const windowWidth = useWindowWidth();

  // Show Get Coins / Redeem here when header is too narrow (threshold depends on chat state)
  const compactBreakpoint = chatContext?.isOpen ? COMPACT_HEADER_BREAKPOINT_CHAT_OPEN : COMPACT_HEADER_BREAKPOINT_CHAT_CLOSED;
  const showHeaderButtons = !isMobileFullScreen && !isCollapsed && windowWidth < compactBreakpoint;

  const { vipTiers, userVipStatus, isLoading: isLoadingVip } = useVip();
  const { data: profile } = useProfileQuery();
  const [openCategories, setOpenCategories] = useState(["Main", "Social Games", "Exclusives", "Support"]);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const { data: maxScReward = null } = useReferralMaxScRewardQuery();

  // Detect PWA to hide "Install App" when already installed
  const isPWA = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  }, []);

  // Fetch category data to conditionally show/hide sidebar items
  const { data: highPlayCategories = [] } = useCategoriesQuery('high_play');
  
  // Get contact channels from unified /user/init to check if Live Chat is enabled
  const contactChannels = useUserInitContactChannels();
  const hasChatEnabled = contactChannels.some(c => c.channel_type === 'chat');
  
  // Support chat visibility context (optional - may not be available in all contexts)
  const supportChatVisibility = useSupportChatVisibilityOptional();
  const { data: liveData } = useLiveCategoriesQuery();
  const liveCasinoEnabled = liveData?.enabled ?? false;

  // Filter out items based on: PWA/desktop, and sections with no categories (or feature disabled)
  const filteredSidebarCategories = useMemo(() => {
    const shouldHideInstall = isPWA || !isMobileFullScreen;
    const hasHighPlayCategories = highPlayCategories.length > 0;
    // Show Live Experience when feature is enabled (regardless of category count)
    const showLiveSection = liveCasinoEnabled;

    return sidebarCategories.map(category => {
      let items = category.items.filter(item => {
        // Hide Install App when in PWA or on desktop
        if (shouldHideInstall && item.path === '/install-app') return false;
        // Hide sections with no categories
        if (item.requiresSection === 'high_play' && !hasHighPlayCategories) return false;
        if (item.requiresSection === 'live' && !showLiveSection) return false;
        return true;
      });
      
      // Add Support Chat item to Support section if chat is enabled
      if (category.title === 'Support' && hasChatEnabled) {
        const supportChatItem: SidebarItem = {
          icon: MessageCircle,
          label: 'Support Chat',
          action: 'support-chat',
        };
        // Insert Support Chat at the beginning (above Help Center)
        items = [supportChatItem, ...items];
      }
      
      return {
        ...category,
        items,
      };
    }).filter(category => category.items.length > 0);
  }, [isPWA, isMobileFullScreen, highPlayCategories.length, liveCasinoEnabled, hasChatEnabled]);

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (profile?.username) {
      return profile.username.slice(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) return `${profile.first_name} ${profile.last_name}`;
    if (profile?.first_name) return profile.first_name;
    if (profile?.username) return profile.username;
    return user?.email?.split('@')[0] || 'User';
  };

  const getUsername = () => {
    if (user?.email) {
      return `@${user.email.split('@')[0]}`;
    }
    return '@user';
  };

  const getCurrentTierData = () => {
    if (!vipTiers.length) return null;
    
    // If user has VIP status, use that tier
    let currentTier = userVipStatus?.current_tier_id 
      ? vipTiers.find(t => t.id.toString() === userVipStatus.current_tier_id?.toString())
      : null;
    
    // If no VIP status or tier not found, default to Iron (level 1)
    if (!currentTier) {
      currentTier = vipTiers.find(t => t.tier_level === 1);
    }
    
    // Find next tier (next higher level, handles gaps from deactivated tiers)
    const nextTier = currentTier 
      ? vipTiers.filter(t => t.tier_level > currentTier.tier_level).sort((a, b) => a.tier_level - b.tier_level)[0] || null
      : null;
    
    // Use monthly VIP points for progress calculation
    const monthlyActivity = userVipStatus?.monthly_vip_points || 0;
    
    // Use points_required for VIP progression (fallback to monthly_gc_required for backward compatibility)
    const nextTierRequirement = nextTier?.points_required ?? nextTier?.monthly_gc_required ?? 0;
    
    // Use backend-calculated progress values for consistency with VIP Rewards page
    const progressPercent = userVipStatus?.progress_percent ?? 0;
    const pointsToNextTier = userVipStatus?.points_to_next_tier ?? Math.max(0, nextTierRequirement - monthlyActivity);
    
    return {
      currentTier,
      nextTier,
      totalWagered: monthlyActivity,
      nextTierRequirement,
      progressPercent,
      pointsToNextTier
    };
  };

  const tierData = getCurrentTierData();


  const toggleCategory = (category: string) => {
    setOpenCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const isActive = (path?: string) => path ? location.pathname === path : false;

  const handleItemClick = (item: SidebarItem) => {
    if (item.disabled) return;
    if (item.action === 'daily-bonus') {
      if (onDailyBonusClick) {
        onDailyBonusClick();
      } else {
        openDailyBonusModal();
        onNavigate?.();
      }
    } else if (item.action === 'chat') {
      if (chatContext) {
        // On mobile (full-screen sidebar), open mobile chat; on desktop, toggle desktop chat
        if (isMobileFullScreen) {
          onNavigate?.();
          requestAnimationFrame(() => chatContext.openMobileChat());
        } else {
          chatContext.toggleChat();
        }
      }
    } else if (item.action === 'support-chat') {
      // Open support chat using the visibility context
      if (supportChatVisibility) {
        supportChatVisibility.openChat();
        onNavigate?.();
      } else {
        // Fallback: navigate to help center if context not available
        navigate('/help-center');
        onNavigate?.();
      }
    } else if (item.path) {
      navigate(item.path);
      onNavigate?.();
    }
  };

  // --- Mobile Full-Screen Native Layout ---
  if (isMobileFullScreen) {
    return (
      <aside className="bg-background flex flex-col">
        <div className="flex-1">
          <div className="px-4 pt-1 pb-6 space-y-5">
            {/* Logo */}
            <div className="flex justify-center pt-1 pb-2">
              <img 
                src={logoSrc} 
                alt="Gold Machine" 
                className="h-10 w-auto object-contain"
                style={{ filter: 'drop-shadow(0 0 8px hsl(var(--glow-gold) / 0.15))' }}
              />
            </div>

            {/* User Profile Card - App style */}
            <div 
              className="flex items-center gap-3.5 p-3.5 rounded-2xl bg-muted/50 active:bg-muted transition-colors"
              onClick={() => { navigate('/profile'); onNavigate?.(); }}
            >
              <Avatar className="h-12 w-12 ring-2 ring-amber-500/40 flex-shrink-0" style={{ boxShadow: '0 0 12px hsl(var(--glow-gold) / 0.2)' }}>
                <AvatarImage src="/default-avatar.jpg" />
                <AvatarFallback className="bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 text-amber-900 font-bold text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground text-[15px] truncate">{getDisplayName()}</div>
                <div className="text-[13px] text-muted-foreground truncate">{user?.email}</div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground/60 flex-shrink-0" />
            </div>

            {/* Get Coins & Redeem - Solid gradient buttons matching currency slider */}
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={onGetCoinsClick || openGetCoinsModal}
                className="relative h-12 rounded-2xl font-bold text-sm overflow-hidden active:scale-[0.96] transition-all duration-200 bg-gradient-to-b from-amber-400 to-amber-600 shadow-md shadow-amber-500/25"
                style={{
                  border: '1.5px solid hsl(var(--casino-gold) / 0.5)',
                  boxShadow: '0 4px 14px hsl(var(--casino-gold-dark) / 0.3), inset 0 1px 0 rgba(255,255,255,0.25)',
                }}
              >
                <span className="relative z-10 text-white font-bold text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">Get Coins</span>
              </button>
              <button 
                onClick={() => {
                  if (onRedeemClick) {
                    onRedeemClick();
                  } else {
                    setIsRedeemModalOpen(true);
                  }
                }}
                className="relative h-12 rounded-2xl font-bold text-sm overflow-hidden active:scale-[0.96] transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 shadow-md shadow-emerald-500/25"
                style={{
                  border: '1.5px solid hsl(var(--casino-green) / 0.5)',
                  boxShadow: '0 4px 14px hsl(var(--casino-green-dark) / 0.3), inset 0 1px 0 rgba(255,255,255,0.25)',
                }}
              >
                <span className="relative z-10 text-white font-bold text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">Redeem</span>
              </button>
            </div>

            {/* VIP & Referral - Side by side cards, equal height */}
            <div className="grid grid-cols-2 gap-3 auto-rows-fr">
              {/* VIP Tier */}
              <div 
                onClick={() => { navigate('/loyalty-club'); onNavigate?.(); }} 
                className="cursor-pointer active:scale-[0.97] transition-transform min-h-[60px]"
              >
                {tierData?.currentTier ? (
                    <div className="rounded-2xl border border-border/50 overflow-hidden bg-muted/30 h-full">
                    <VipTierDisplay 
                      key={`vip-tier-${userVipStatus?.current_tier_id}-${tierData.progressPercent}-${tierData.pointsToNextTier}`}
                      tierName={tierData.currentTier.tier_name}
                      tierLevel={tierData.currentTier.tier_level}
                      tierColor={tierData.currentTier.tier_color}
                      tierReachRewardGc={tierData.currentTier.tier_reach_reward_gc || 0}
                      tierReachRewardSc={tierData.currentTier.tier_reach_reward_sc || 0}
                      weeklyRewardPercentage={tierData.currentTier.weekly_reward_percentage || 0}
                      maxWeeklyRewardGc={tierData.currentTier.max_weekly_reward_gc || 0}
                      maxWeeklyRewardSc={tierData.currentTier.max_weekly_reward_sc || 0}
                      monthlyGcRequired={tierData.currentTier.monthly_gc_required}
                      vipAccess={tierData.currentTier.vip_access || tierData.currentTier.tier_level >= 3}
                      isCurrentTier={true}
                      showDetails={false}
                      className="h-full"
                      nextTierRequirement={tierData.nextTierRequirement}
                      totalWagered={tierData.totalWagered}
                      backendProgressPercent={tierData.progressPercent}
                      backendPointsToNextTier={tierData.pointsToNextTier}
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/50 bg-muted/30 p-3 h-full">
                    <div className="h-4 w-16 bg-muted animate-pulse rounded mb-2" />
                    <div className="h-3 w-12 bg-muted animate-pulse rounded" />
                  </div>
                )}
              </div>

              {/* Referral Banner */}
              <div 
                className="rounded-2xl cursor-pointer overflow-hidden active:scale-[0.97] transition-transform h-full"
                onClick={() => { navigate('/refer-friends'); onNavigate?.(); }}
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--casino-green-dark) / 0.30) 0%, hsl(var(--casino-green-dark) / 0.45) 50%, hsl(var(--casino-green-dark) / 0.20) 100%)',
                  border: '1px solid hsl(var(--casino-green) / 0.25)',
                }}
              >
                <div className="px-3 py-2.5 flex items-center gap-2.5 h-full">
                  <div 
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-emerald-400 to-emerald-600"
                    style={{ boxShadow: '0 4px 12px hsl(var(--glow-green) / 0.4)' }}
                  >
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold text-[10px] text-emerald-300 uppercase tracking-wider">Refer & Earn</div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-[9px] text-emerald-200/50">up to</span>
                      <span className="font-extrabold text-sm text-white">
                        {maxScReward !== null ? maxScReward.toLocaleString() : '...'}
                      </span>
                      <span className="text-[10px] font-bold text-emerald-300">SC</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Sections - Flat native grouped list */}
            {filteredSidebarCategories.map((category, catIndex) => {
              const categoryIcons: Record<string, string> = {
                'Main': 'text-muted-foreground',
                'Social Games': 'text-sky-400',
                'Exclusives': 'text-purple-400',
                'Support': 'text-emerald-400',
              };
              const labelColor = categoryIcons[category.title] || 'text-muted-foreground';

              return (
                <div key={category.title}>
                  {/* Section header */}
                  <div className="flex items-center px-1 mb-1.5">
                    <span className={`text-[11px] font-bold uppercase tracking-widest ${labelColor}`}>
                      {category.title}
                    </span>
                  </div>
                  {/* Grouped card */}
                  <div className="rounded-2xl bg-muted/30 border border-border/50 overflow-hidden divide-y divide-border/30">
                    {category.items.map((item, index) => {
                      const active = isActive(item.path);
                      return (
                        <button
                          key={index}
                          onClick={() => handleItemClick(item)}
                          className={`w-full flex items-center gap-3.5 px-4 py-3.5 text-left transition-colors ${
                            item.disabled ? 'opacity-40 cursor-default' : 'active:bg-muted'
                          } ${active ? 'bg-muted/60' : ''}`}
                          disabled={item.disabled}
                        >
                          <item.icon className={`h-[22px] w-[22px] flex-shrink-0 ${
                            active ? 'text-foreground' : 'text-muted-foreground'
                          }`} />
                          <span className={`text-[15px] flex-1 ${
                            active ? 'text-foreground font-semibold' : 'text-foreground/80'
                          }`}>
                            {item.label}
                          </span>
                          {item.badge && (
                            <Badge className={`text-[10px] px-1.5 py-0.5 border-0 ${item.disabled ? 'bg-muted-foreground/50 text-foreground/70' : 'bg-red-500 text-white'}`}>
                              {item.badge}
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          </div>
        </div>

        {/* Theme & Sign Out - Sticky at bottom, always visible */}
        <div className="sticky bottom-0 border-t border-border px-4 py-3 bg-background">
          <div className="flex items-center gap-2">
            <ColorThemeSelectorMini showLabel className="flex-1 bg-muted/50 border border-border hover:border-amber-500/40 hover:bg-muted" />
            <button
              onClick={() => { signOut(); onNavigate?.(); }}
              className="h-10 w-10 flex items-center justify-center rounded-xl bg-muted/30 border border-border active:bg-muted transition-colors flex-shrink-0"
            >
              <LogOut className="h-5 w-5 text-muted-foreground/70" />
            </button>
          </div>
        </div>

        <RedeemModal 
          isOpen={isRedeemModalOpen} 
          onClose={() => setIsRedeemModalOpen(false)} 
        />
      </aside>
    );
  }

  // --- Desktop Sidebar Layout ---
  return (
    <TooltipProvider delayDuration={100}>
    <aside className="h-full bg-sidebar border-r border-sidebar-border flex flex-col shadow-2xl transition-all duration-300 relative">
      {/* Ambient background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-48 bg-gradient-to-b from-amber-500/[0.06] via-purple-500/[0.03] to-transparent blur-2xl" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-48 bg-gradient-to-t from-emerald-500/[0.05] to-transparent blur-2xl" />
        {/* Side edge highlight */}
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-amber-500/10 via-purple-500/5 to-emerald-500/10" />
      </div>
      {/* Collapse/Expand Toggle Button - Floating on edge */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          className="absolute -right-3 top-20 z-50 w-6 h-6 rounded-full bg-card border border-border hover:border-amber-500/50 hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-amber-400 transition-all shadow-lg group"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft className={`h-3.5 w-3.5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>
      )}
      
      {/* Logo at top - Clickable to go to Lobby */}
      <div className={`border-b border-sidebar-border flex-shrink-0 transition-all duration-300 relative ${isCollapsed ? 'p-2' : 'p-4'}`}>
        {/* Logo glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-16 bg-amber-500/[0.06] blur-2xl rounded-full" />
        </div>
        <div 
          className="flex items-center justify-center w-full cursor-pointer hover:opacity-90 transition-opacity relative"
          onClick={() => navigate('/home')}
        >
          <img 
            src={logoSrc} 
            alt="Gold Machine" 
            className={`object-contain transition-all duration-300 ${
              isCollapsed ? 'h-12 w-12' : 'h-14 w-full max-w-[200px]'
            }`}
            style={{ filter: 'drop-shadow(0 0 8px hsl(var(--glow-gold) / 0.15))' }}
          />
        </div>
      </div>
      
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        <div className={`space-y-4 flex-1 transition-all duration-300 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          {/* User Profile Card */}
        {isCollapsed ? (
          // Collapsed: Avatar card with tooltip
          <Tooltip>
            <TooltipTrigger asChild>
              <div 
                className="flex justify-center cursor-pointer group p-2 rounded-xl bg-sidebar-accent border border-sidebar-border hover:border-amber-500/30 hover:bg-muted transition-all shadow-lg hover:shadow-amber-500/10"
                onClick={() => navigate('/profile')}
              >
                <Avatar className="h-10 w-10 ring-2 ring-amber-500/30 group-hover:ring-amber-400/50 shadow-lg transition-all">
                  <AvatarImage src="/default-avatar.jpg" />
                  <AvatarFallback className="bg-gradient-to-br from-amber-600 via-amber-500 to-yellow-500 text-white font-bold shadow-lg">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-popover border-border text-popover-foreground">
              <p className="font-semibold">{getDisplayName()}</p>
              <p className="text-muted-foreground text-xs">{user?.email}</p>
              <p className="text-blue-400 text-xs mt-1">Click to view profile</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          // Expanded: Profile card with clear clickable styling
          <div 
            className="cursor-pointer group rounded-xl px-3 py-3 bg-sidebar-accent border border-sidebar-border hover:border-amber-400/40 hover:bg-muted transition-all active:scale-[0.98] relative"
            style={{ boxShadow: '0 4px 20px hsl(var(--glow-gold) / 0.08)' }}
            onClick={() => navigate('/profile')}
          >
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11 ring-2 ring-amber-500/40 group-hover:ring-amber-400/60 transition-all flex-shrink-0" style={{ boxShadow: '0 0 16px hsl(var(--glow-gold) / 0.3)' }}>
                <AvatarImage src="/default-avatar.jpg" />
                <AvatarFallback className="bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 text-amber-900 font-bold text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sidebar-foreground text-sm truncate group-hover:text-amber-100 transition-colors">
                  {getDisplayName()}
                </div>
                <div className="text-xs text-muted-foreground truncate group-hover:text-muted-foreground/80 transition-colors">
                  {user?.email}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground/70 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </div>
          </div>
        )}

        {/* VIP Tier & Referral Cards - Desktop sidebar stacked */}
        {!isCollapsed && (
          <div className="space-y-4">
            {/* VIP Tier Display - Enhanced Casino Style */}
            <div 
              onClick={() => navigate('/loyalty-club')} 
              className="cursor-pointer group h-full"
            >
              {tierData?.currentTier ? (
                <div className="relative overflow-hidden rounded-xl border border-sidebar-border group-hover:border-amber-500/40 transition-all shadow-lg group-hover:shadow-amber-500/20 h-full bg-sidebar-accent">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/[0.06] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <VipTierDisplay 
                    key={`vip-tier-${userVipStatus?.current_tier_id}-${tierData.progressPercent}-${tierData.pointsToNextTier}`}
                    tierName={tierData.currentTier.tier_name}
                    tierLevel={tierData.currentTier.tier_level}
                    tierColor={tierData.currentTier.tier_color}
                    tierReachRewardGc={tierData.currentTier.tier_reach_reward_gc || 0}
                    tierReachRewardSc={tierData.currentTier.tier_reach_reward_sc || 0}
                    weeklyRewardPercentage={tierData.currentTier.weekly_reward_percentage || 0}
                    maxWeeklyRewardGc={tierData.currentTier.max_weekly_reward_gc || 0}
                    maxWeeklyRewardSc={tierData.currentTier.max_weekly_reward_sc || 0}
                    monthlyGcRequired={tierData.currentTier.monthly_gc_required}
                    vipAccess={tierData.currentTier.vip_access || tierData.currentTier.tier_level >= 3}
                    isCurrentTier={true}
                    showDetails={false}
                    className="relative z-10 h-full"
                    nextTierRequirement={tierData.nextTierRequirement}
                    totalWagered={tierData.totalWagered}
                    backendProgressPercent={tierData.progressPercent}
                    backendPointsToNextTier={tierData.pointsToNextTier}
                  />
                </div>
              ) : (
                // Loading skeleton
                <div className="relative p-3 border rounded-lg bg-gradient-to-r from-card to-muted/30 border-border h-full">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-muted animate-pulse w-8 h-8"></div>
                      <div>
                        <div className="h-4 w-16 bg-muted animate-pulse rounded mb-1"></div>
                        <div className="h-3 w-12 bg-muted animate-pulse rounded"></div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="h-3 w-8 bg-muted animate-pulse rounded mb-1"></div>
                      <div className="h-4 w-6 bg-muted animate-pulse rounded"></div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="h-3 w-16 bg-muted animate-pulse rounded"></div>
                      <div className="h-3 w-8 bg-muted animate-pulse rounded"></div>
                    </div>
                    <div className="w-full bg-border rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-muted animate-pulse w-1/3"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Referral Banner - Subtle Design matching sidebar */}
            <div 
              className="relative rounded-xl cursor-pointer overflow-hidden group h-full mb-4"
              onClick={() => navigate('/refer-friends')}
              style={{
                background: 'linear-gradient(135deg, hsl(var(--casino-green-dark) / 0.35) 0%, hsl(var(--casino-green-dark) / 0.5) 50%, hsl(var(--casino-green-dark) / 0.25) 100%)',
                boxShadow: '0 4px 20px hsl(var(--glow-green) / 0.2), 0 0 40px hsl(var(--glow-green) / 0.08)',
                border: '1px solid hsl(var(--casino-green) / 0.3)',
              }}
            >
              {/* Hover brightening */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-emerald-400/10 via-transparent to-transparent"></div>
              
              {/* Content */}
              <div className="relative z-10 px-2.5 py-2.5 flex items-center gap-2.5 h-full">
                {/* Icon */}
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-emerald-400 to-emerald-600"
                  style={{ boxShadow: '0 4px 15px hsl(var(--glow-green) / 0.5)' }}
                >
                  <Users className="h-4.5 w-4.5 text-white drop-shadow-md" />
                </div>
                
                {/* Text content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="font-black text-[10px] text-emerald-300 uppercase tracking-wider">Refer & Earn</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-[9px] text-emerald-200/50">up to</span>
                    <span className="font-black text-sm text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]">
                      {maxScReward !== null ? maxScReward.toLocaleString() : '...'}
                    </span>
                    <span className="text-[10px] font-bold text-emerald-300">SC</span>
                  </div>
                </div>
                
                {/* Arrow indicator */}
                <ChevronRight className="h-4 w-4 text-emerald-400/50 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
              </div>
            </div>
          </div>
        )}

        {/* Get Coins & Redeem — shown here when header is too narrow (chat open + small viewport) */}
        {showHeaderButtons && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onGetCoinsClick || openGetCoinsModal}
              className="h-10 rounded-xl font-semibold text-sm bg-muted/60 border border-border hover:bg-muted text-foreground transition-all active:scale-[0.97]"
            >
              Get Coins
            </button>
            <button
              onClick={() => {
                if (onRedeemClick) {
                  onRedeemClick();
                } else {
                  setIsRedeemModalOpen(true);
                }
              }}
              className={`h-10 rounded-xl font-semibold text-sm transition-all active:scale-[0.97] border ${
                selectedCurrency === 'GC'
                  ? 'bg-gradient-to-b from-amber-400/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-500 text-white border-amber-400/30'
                  : 'bg-gradient-to-b from-emerald-400/90 to-emerald-600/90 hover:from-emerald-400 hover:to-emerald-500 text-white border-emerald-400/30'
              }`}
            >
              Redeem
            </button>
          </div>
        )}

        {/* Navigation - Colorful Casino Style */}
        <nav className={`space-y-4 ${isCollapsed ? 'space-y-2' : ''}`}>
          {filteredSidebarCategories.map((category, catIndex) => {
            // Color themes for each category
            const categoryColors = [
              { text: 'text-red-400', icon: 'text-red-400/70', hover: 'hover:bg-red-500/[0.08]', separator: 'via-red-500/20', activeBg: 'bg-red-500/20', activeBorder: 'border-red-500/40', activeIcon: 'text-red-400', activeText: 'text-sidebar-foreground', activeGlow: '0 0 20px rgba(239,68,68,0.2)' }, // Main
              { text: 'text-sky-400', icon: 'text-sky-400/70', hover: 'hover:bg-sky-500/[0.08]', separator: 'via-sky-500/20', activeBg: 'bg-sky-500/20', activeBorder: 'border-sky-500/40', activeIcon: 'text-sky-400', activeText: 'text-sidebar-foreground', activeGlow: '0 0 20px rgba(14,165,233,0.2)' }, // Social Games
              { text: 'text-purple-400', icon: 'text-purple-400/70', hover: 'hover:bg-purple-500/[0.08]', separator: 'via-purple-500/20', activeBg: 'bg-purple-500/20', activeBorder: 'border-purple-500/40', activeIcon: 'text-purple-400', activeText: 'text-sidebar-foreground', activeGlow: '0 0 20px rgba(168,85,247,0.2)' }, // Exclusives
              { text: 'text-emerald-400', icon: 'text-emerald-400/70', hover: 'hover:bg-emerald-500/[0.08]', separator: 'via-emerald-500/20', activeBg: 'bg-emerald-500/20', activeBorder: 'border-emerald-500/40', activeIcon: 'text-emerald-400', activeText: 'text-sidebar-foreground', activeGlow: '0 0 20px rgba(16,185,129,0.2)' } // Support
            ];
            const catColor = categoryColors[catIndex] || categoryColors[0];
            
            // Collapsed mode: show just icons without category headers
            if (isCollapsed) {
              return (
                <div key={category.title} className="space-y-1">
                  {/* Collapsed separator line with color */}
                  <div className={`h-px bg-gradient-to-r from-transparent ${catColor.separator} to-transparent my-2`} />
                  {category.items.map((item, index) => {
                    const active = isActive(item.path);
                    const isGoldTheme = getThemeColors().primary === 'hsl(var(--casino-gold))';
                    
                    return (
                      <Tooltip key={index}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            disabled={item.disabled}
                            onClick={() => handleItemClick(item)}
                            className={`w-full justify-center p-2 h-10 transition-all duration-200 relative rounded-lg ${
                              item.disabled ? 'opacity-40 cursor-default' :
                              active
                                ? `${catColor.activeBg} border ${catColor.activeBorder} ${catColor.activeText}`
                                : `text-sidebar-foreground/80 hover:text-sidebar-foreground ${catColor.hover} border border-transparent`
                            }`}
                            style={active && !item.disabled ? { boxShadow: catColor.activeGlow } : undefined}
                          >
                            <item.icon className={`h-5 w-5 ${
                              active
                                ? catColor.activeIcon
                                : `${catColor.icon} group-hover:text-sidebar-foreground`
                            }`} />
                            {item.badge && !item.disabled && (
                              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="bg-popover border-border text-popover-foreground">
                          {item.label}{item.disabled ? ' (Coming Soon)' : ''}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            }
            
            // Expanded mode: show full navigation
            return (
              <div key={category.title}>
                {/* Category separator */}
                {catIndex > 0 && <div className={`h-px bg-gradient-to-r from-transparent ${catColor.separator} to-transparent mb-3`} />}
                <Collapsible 
                  open={openCategories.includes(category.title)} 
                  onOpenChange={() => toggleCategory(category.title)}
                >
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className={`w-full justify-between ${catColor.text} hover:text-sidebar-foreground mb-2 ${catColor.hover} transition-all rounded-lg`}
                    >
                      <span className="text-[11px] font-black uppercase tracking-widest">{category.title}</span>
                      {openCategories.includes(category.title) ? 
                        <ChevronDown className="h-3.5 w-3.5" /> : 
                        <ChevronRight className="h-3.5 w-3.5" />
                      }
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1">
                    {category.items.map((item, index) => {
                      const active = isActive(item.path);
                      const isGoldTheme = getThemeColors().primary === 'hsl(var(--casino-gold))';
                      
                      return (
                        <Button
                          key={index}
                          variant="ghost"
                          onClick={() => handleItemClick(item)}
                          disabled={item.disabled}
                          className={`w-full justify-start transition-all duration-200 relative group rounded-lg ${
                            item.disabled ? 'opacity-40 cursor-default' :
                            active
                              ? `${catColor.activeBg} border ${catColor.activeBorder} ${catColor.activeText} font-semibold`
                              : `text-sidebar-foreground/80 hover:text-sidebar-foreground ${catColor.hover} border border-transparent`
                          }`}
                          style={active && !item.disabled ? { boxShadow: catColor.activeGlow } : undefined}
                        >
                          <item.icon className={`h-4 w-4 mr-3 ${
                            active
                              ? catColor.activeIcon
                              : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground transition-colors'
                          }`} />
                          <span className="flex-1 text-left">{item.label}</span>
                          {item.badge && (
                            <Badge className={`ml-2 border shadow-lg text-[10px] px-1.5 py-0.5 ${
                              item.disabled ? 'bg-muted-foreground/50 text-foreground/70 border-muted-foreground/30' :
                              active
                                ? 'bg-red-600 text-white border-red-500'
                                : 'bg-red-500 text-white border-red-400'
                            }`}>
                              {item.badge}
                            </Badge>
                          )}
                        </Button>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </nav>

        </div>
      </div>

      {/* Theme Selector & Sign Out - Fixed at bottom, always visible */}
      <div className={`border-t border-sidebar-border flex-shrink-0 transition-all duration-300 relative z-20 ${isCollapsed ? 'p-2' : 'p-4'}`}>
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-1">
            <ColorThemeSelectorMini className="h-9 w-9 rounded-lg bg-sidebar-accent border border-sidebar-border hover:border-amber-500/40 hover:bg-muted" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={signOut}
                  className="h-7 w-7 text-muted-foreground/70 hover:text-red-400 transition-colors"
                  size="icon"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-popover border-border text-popover-foreground">
                Sign Out
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <ColorThemeSelectorMini showLabel className="flex-1 rounded-lg bg-sidebar-accent border border-sidebar-border hover:border-amber-500/40 hover:bg-muted" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={signOut}
                  className="h-8 w-8 text-muted-foreground/70 hover:text-red-400 hover:bg-red-500/[0.08] transition-colors flex-shrink-0"
                  size="icon"
                  title="Sign Out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-popover border-border text-popover-foreground">
                Sign Out
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
      
      <RedeemModal 
        isOpen={isRedeemModalOpen} 
        onClose={() => setIsRedeemModalOpen(false)} 
      />
    </aside>
    </TooltipProvider>
  );
};
